import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { downloadProtected } from "@/components/ImportDialog";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, FileText, Mail, TrendingUp, TrendingDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { eur } from "@/lib/constants";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const fmtMes = (m) => {
  const [y, mm] = m.split("-");
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
};

const Variacao = ({ dif, pct, anterior, testid }) => {
  if (!dif && !anterior) return <span className="text-slate-300" data-testid={testid}>—</span>;
  if (pct === null || pct === undefined) return <span className="text-sky-600 font-semibold" data-testid={testid}>novo</span>;
  const up = dif > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const cls = dif === 0 ? "text-slate-500" : up ? "text-amber-700" : "text-emerald-700";
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${cls}`} data-testid={testid}>
      {dif !== 0 && <Icon size={12} />}{dif > 0 ? "+" : ""}{eur(dif)} <span className="font-normal">({pct > 0 ? "+" : ""}{String(pct).replace(".", ",")}%)</span>
    </span>
  );
};

export const EncomendasResumo = () => {
  const [mes, setMes] = useState("");
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();

  const enviar = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/encomendas/resumo-mensal/enviar", null, { params: { mes: data.mes } });
      toast.success(`Resumo enviado para ${res.destino}`);
    } catch (err) { toast.error(apiError(err)); }
    setSending(false);
  };

  useEffect(() => {
    api.get("/encomendas/resumo-mensal", { params: mes ? { mes } : {} }).then((r) => setData(r.data));
  }, [mes]);

  const exportar = async (formato) => {
    try {
      await downloadProtected(`/encomendas/resumo-mensal/export?formato=${formato}&mes=${data.mes}`, `resumo_compras_${data.mes}.${formato}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!data) return <div className="text-sm text-slate-400 p-6">A carregar…</div>;

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="resumo-mensal">
      <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <Select value={data.mes} onValueChange={setMes}>
          <SelectTrigger className="h-8 w-48 text-xs capitalize" data-testid="resumo-mes-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {data.meses.map((m) => <SelectItem key={m} value={m} className="capitalize">{fmtMes(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => exportar("xlsx")} data-testid="resumo-export-xlsx"><FileSpreadsheet size={14} className="text-emerald-600" /> Excel</Button>
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => exportar("pdf")} data-testid="resumo-export-pdf"><FileText size={14} className="text-rose-600" /> PDF</Button>
          {user?.role === "admin" && (
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={enviar} disabled={sending} data-testid="resumo-enviar-email"><Mail size={14} className="text-indigo-600" /> {sending ? "A enviar…" : "Enviar por email"}</Button>
          )}
        </div>
        <div className="text-right ml-auto">
          <div className="text-[10px] uppercase text-slate-400 font-semibold">Total do mês</div>
          <div className="text-lg font-bold text-slate-900" data-testid="resumo-total">{eur(data.total)} <span className="text-xs font-normal text-slate-400">· {data.encomendas} encomenda(s)</span></div>
          <div className="text-[11px] text-slate-500" data-testid="resumo-total-anterior">
            vs {fmtMes(data.mes_anterior)}: {eur(data.total_anterior)} ·{" "}
            <Variacao dif={data.diferenca} pct={data.variacao_pct} anterior={data.total_anterior} testid="resumo-total-variacao" />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Fornecedor</th>
              <th className="text-center px-4 py-2 font-semibold">Encomendas</th>
              <th className="text-center px-4 py-2 font-semibold">Arquivadas</th>
              <th className="text-center px-4 py-2 font-semibold">Linhas</th>
              <th className="text-right px-4 py-2 font-semibold">Total custo</th>
              <th className="text-right px-4 py-2 font-semibold whitespace-nowrap">Mês anterior</th>
              <th className="text-right px-4 py-2 font-semibold whitespace-nowrap">Variação</th>
            </tr>
          </thead>
          <tbody>
            {data.fornecedores.map((f, i) => (
              <tr key={f.fornecedor_id || i} className="border-t border-slate-100" data-testid={`resumo-row-${i}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{f.fornecedor}</td>
                <td className="px-4 py-2 text-center">{f.encomendas}</td>
                <td className="px-4 py-2 text-center text-slate-500">{f.arquivadas}</td>
                <td className="px-4 py-2 text-center">{f.linhas}</td>
                <td className="px-4 py-2 text-right font-semibold">{eur(f.total)}</td>
                <td className="px-4 py-2 text-right text-slate-500">{eur(f.total_anterior)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap"><Variacao dif={f.diferenca} pct={f.variacao_pct} anterior={f.total_anterior} testid={`resumo-variacao-${i}`} /></td>
              </tr>
            ))}
            {!data.fornecedores.length && (
              <tr><td colSpan={7} className="text-center text-slate-400 py-8">Sem encomendas neste mês.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
